import type { Personenangabe } from "@dj/domain/gemeinsam";
import { useDokument, useSammlung, type Zeile } from "../plattform/lesemodelle";
import { useSitzung } from "../plattform/sitzung";

/**
 * Lesemodelle, aus denen ein Gerät die Lage einer Drückjagd kennt (lesemodelle.md §3, §4, §6), und der Rahmen einer
 * Ansicht der Drückjagd (Zustandsband, `main`), den `App.tsx` liefert: Die Ansicht der Drückjagd gehört dem Kontext
 * Drückjagd (ADR-0029, Modulgrenzen der PWA).
 */
export type { Rahmen, RahmenProps } from "../plattform/rahmen";

const eintragspfad = (uid: string | null, dj: string) => (uid ? `personen/${uid}/meine_drueckjagden/${dj}` : null);

/** Neutrale Rollen der Person in dieser Drückjagd und das Revier ihrer Einteilung (Meine Drückjagden). */
export function useMeinEintrag(drueckjagdId: string): { rollen: string[]; revier: string | null; bekannt: boolean } {
  const { uid } = useSitzung();
  const e = useDokument(eintragspfad(uid, drueckjagdId));
  return {
    rollen: (e.daten?.["Rollen"] as string[] | undefined) ?? [],
    revier: (e.daten?.["_kennungen"] as Record<string, string> | undefined)?.["Revier"] ?? null,
    bekannt: !!e.daten || !e.laedt,
  };
}

/** Meine Aufgaben: eigener Stand (`_kennungen.Stand`), eigene Treibergruppe, die Person selbst (`_person`). */
export function useMeineAufgaben(drueckjagdId: string) {
  const { uid } = useSitzung();
  const a = useDokument(uid ? `drueckjagden/${drueckjagdId}/meine_aufgaben/${uid}` : null);
  const kennungen = (a.daten?.["_kennungen"] as Record<string, string> | undefined) ?? {};
  return {
    laedt: a.laedt && !a.daten,
    standId: kennungen["Stand"] ?? null,
    standnummer: (a.daten?.["Stand"] as string | null | undefined) ?? null,
    treibergruppe: (a.daten?.["Treibergruppe"] as string | null | undefined) ?? null,
    person: (a.daten?.["_person"] as Personenangabe | undefined) ?? null,
    naechsterRettungspunkt: (a.daten?.["Nächster Rettungspunkt"] as { Kennung: string; Lage: string | null } | null | undefined) ?? null,
  };
}

export function useDrueckjagdSammlung(drueckjagdId: string, teil: string): Zeile[] {
  return useSammlung(`drueckjagden/${drueckjagdId}/${teil}`).daten;
}

/** Lesemodell eines Reviers in der Drückjagd (lesemodelle.md §4). */
export function useRevierSammlung(drueckjagdId: string, revier: string | null, teil: string): Zeile[] {
  return useSammlung(revier ? `drueckjagden/${drueckjagdId}/reviere/${revier}/${teil}` : null).daten;
}

export function personAus(w: unknown): Personenangabe | null {
  return w && typeof w === "object" && "Kennung" in w ? (w as Personenangabe) : null;
}

export function positionAus(w: unknown): { Breite: number; Länge: number } | null {
  return w && typeof w === "object" && "Breite" in w ? (w as { Breite: number; Länge: number }) : null;
}

/** `<Breite>, <Länge>` mit sechs Nachkommastellen (README §4.3), ohne Position „—“. */
export function positionText(p: { Breite: number; Länge: number } | null | undefined): string {
  return p ? `${p.Breite.toFixed(6)}, ${p.Länge.toFixed(6)}` : "—";
}
